import { Box, Center, Group, Stack, Text, ThemeIcon, Title } from "@mantine/core";
import type { ReactNode } from "react";

/**
 * EmptyStateSize selects the block's density AND its orientation. The default "md"
 * is the full page/panel treatment; "compact" tightens the icon, heading, vertical
 * padding, and column width to suit a narrow sidenav (~320px) where the block sits
 * above an add affordance rather than filling an empty view; "inline" drops the
 * centered column entirely for a left-aligned row (the glyph beside the title, the
 * description beneath it), so the block reads as one quiet hint inside a dense list
 * rather than as a placeholder view holding that list's space open.
 */
export type EmptyStateSize = "compact" | "inline" | "md";

/**
 * EmptyStateColumnLayout holds the measurements of the two CENTERED presets: the
 * icon size, the heading order + its font-size token, the description font size, the
 * vertical padding token, and the column max width. `titleSize` carries the heading's
 * font-size token: the "md" preset uses "h4" (identical to its order-4 default, so
 * the full block is unchanged) and "compact" steps it down to "sm".
 */
interface EmptyStateColumnLayout {
  descriptionSize: string;
  iconSize: number;
  maxWidth: number;
  orientation: "column";
  padding: string;
  titleOrder: 4 | 5;
  titleSize: string;
}

/**
 * EmptyStateRowLayout holds the ROW preset's measurements. It is deliberately a
 * different shape rather than the column layout with optional fields: the row's
 * glyph box, its heading treatment and its column width are not smaller versions of
 * the centered block's, they are the ask bar's, so there is nothing for the two to
 * share beyond the padding step and the description tier.
 *
 * `iconSize` is a NAMED ThemeIcon token rather than the column presets' pixel number,
 * because the row's glyph box is measured against the ask bar's chip beside it (both
 * Mantine size steps) rather than against a centered block's optical weight.
 */
interface EmptyStateRowLayout {
  descriptionSize: string;
  iconSize: string;
  orientation: "row";
  padding: string;
}

/**
 * EmptyStateLayout is the union of the two orientations, so the render branch that
 * reads a column measurement is the one the compiler already knows is a column.
 */
type EmptyStateLayout = EmptyStateColumnLayout | EmptyStateRowLayout;

/**
 * emptyStateLayout pairs each size with its measurements. The "md" preset is the
 * full-size block (a 36px icon, an order-4 title at its default size, a sm description,
 * xl padding, a 300px column); "compact" shrinks each (and drops the title to sm and the
 * description to xs), so the block reads as a quiet hint in a narrow sidenav at the same
 * scale as the dense form controls around it; "inline" turns the column on its side so an
 * explainer inside a list costs roughly one row of height instead of a screenful.
 */
const emptyStateLayout = {
  compact: {
    descriptionSize: "xs",
    iconSize: 28,
    maxWidth: 260,
    orientation: "column",
    padding: "md",
    titleOrder: 5,
    titleSize: "sm",
  },
  inline: {
    descriptionSize: "xs",
    // A step up from the ask bar's chip, because this glyph has no chip: a filled
    // 22px chip and a bare 22px glyph do not carry the same weight beside the same
    // line, and the bare one has to make the difference up in size.
    iconSize: "md",
    orientation: "row",
    padding: "xs",
  },
  md: {
    descriptionSize: "sm",
    iconSize: 36,
    maxWidth: 300,
    orientation: "column",
    padding: "xl",
    titleOrder: 4,
    titleSize: "h4",
  },
} as const satisfies Record<EmptyStateSize, EmptyStateLayout>;

/**
 * EmptyStateProps drive the shared "nothing here" block: a required icon (the already-sized glyph,
 * shown backgroundless in gray) and title, an optional dimmed description, an optional action slot
 * (a button or link) rendered under the copy, and an optional `size` selecting the full ("md", the
 * default), "compact", or "inline" density. All copy is supplied by the caller so the block carries
 * no localized strings of its own.
 */
export interface EmptyStateProps {
  action?: ReactNode;
  description?: string | undefined;
  icon: ReactNode;
  size?: EmptyStateSize | undefined;
  title: string;
}

/**
 * EmptyState is the "nothing here" block: a backgroundless gray icon over a title, an optional
 * dimmed description, and an optional action.
 *
 * The third state a list has, and the reason it is published beside {@link ListSkeleton} and
 * {@link ErrorAlert}: a list is loading, or it failed, or it has nothing in it, and a consumer
 * assembling one from published rows needs all three to read as one system. The icon arrives
 * pre-sized so a caller picks the glyph and its scale, and the action slot lets them offer a
 * recovery (create, retry) without this block knowing what it does. Every string is the
 * caller's: this package ships no i18n runtime. `size` defaults to "md".
 *
 * Reach for it for the generic "no results / nothing yet" surface. It is NOT the shape for a screen
 * that invites or gates (a hero introducing a first action, a notice that an action is not part
 * of the viewer's agreement, a branded interstitial), each of which owns its own composition
 * rather than dressing up this neutral block.
 */
export const EmptyState = ({ action, description, icon, size = "md", title }: EmptyStateProps) => {
  const layout = emptyStateLayout[size];

  const body = (
    <>
      {description ? (
        <Text c="dimmed" size={layout.descriptionSize}>
          {description}
        </Text>
      ) : null}
      {action}
    </>
  );

  // The row orientation is the dense-list treatment, and it is built to the SAME
  // measurements as the co-pilot ask bar it sits a few rows away from: a gray glyph
  // leading a medium-weight xs line over a dimmed one, on the xs gap-and-padding step.
  // Two sets of measurements would make them visibly different objects (a large transparent
  // glyph beside a heading, against a small filled chip beside a label), which reads as two
  // systems stacked in one panel.
  //
  // The glyph is `transparent`, matching the centered presets: this block explains an
  // ABSENCE, and a filled chip is an object on the surface; inside a bordered list box
  // it would read as a row that had lost its content rather than as a note about the box
  // being empty. The ask bar keeps its chip because it is a control.
  //
  // The chip is anchored to the TITLE's line, not centred against the block: the
  // description runs to one line or three depending on the copy and the panel width, and
  // a centred chip drifts down with it, so the same block reads with its glyph beside
  // the heading in one language and floating in the margin in another. Top-aligned it
  // stays where the eye enters. wrap="nowrap" keeps it on that line however far the
  // description runs.
  if (layout.orientation === "row") {
    return (
      <Group align="flex-start" gap="xs" py={layout.padding} wrap="nowrap">
        <ThemeIcon color="gray" size={layout.iconSize} variant="transparent">
          {icon}
        </ThemeIcon>
        <Box flex={1}>
          <Text fw={500} size="xs">
            {title}
          </Text>
          {body}
        </Box>
      </Group>
    );
  }

  return (
    <Center py={layout.padding}>
      <Stack align="center" gap="sm" maw={layout.maxWidth} ta="center">
        <ThemeIcon color="gray" size={layout.iconSize} variant="transparent">
          {icon}
        </ThemeIcon>
        <Title fz={layout.titleSize} order={layout.titleOrder}>
          {title}
        </Title>
        {body}
      </Stack>
    </Center>
  );
};
